import cx from "classnames";
import { useRef, useState } from "react";

import { addResultToTalentPool, drainTalentPool, searchTalent } from "./TalentPool.utils";
import { Button, Flex, SearchMenu, Text } from "@theorgchartdev/storybook";
import { DropArea } from "@/appComponents/Blocks/Composites/DragAndDrop/DropArea";
import { getHoverTipContent } from "@/Utils/Misc";
import { TalentDetail } from "./TalentDetail";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { usePlanningCache } from "../usePlanningCache";
import { appstr } from "@coreapp/utils/appstr";
import { FormatEmployeeDictLabel } from "@coreapp/utils/FormatEmployeeDictLabel";
import { enumObjectTypes } from "@coreapp/constants";

export const TalentPool = () => {
  const [isSearching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const wsMgrContext = useWSMgrContext();
  const wsMgr = wsMgrContext?.wsMgr.state;
  const planMgrContext = usePlanManagerContext();
  const planMgr = planMgrContext?.planManager.state;

  const doc = wsMgr?.GetDoc();
  const objDB = doc?.GetObjDB();
  const boxClass = objDB?.GetClassByName("Box");
  const IDFieldDef = boxClass?.GetRecordIDFieldDef();
  const IDField = IDFieldDef?.GetFieldName();

  const talentPool = planMgr?.GetTalentPool() ?? [];
  const talentPoolIDs = talentPool.map(talent => ({
    refId: talent[IDField],
    objType: enumObjectTypes.holder
  }));
  usePlanningCache(talentPoolIDs);

  if (!wsMgr || !planMgr || !doc) {
    return null;
  }

  const handleDragEnd = panel => {
    panel.GetDragElement().fParentPanel.fOverTalentPanel = true;
  };

  const handleInput = async query => {
    if (isSearching) {
      return;
    }

    setSearching(true);
    const results = await searchTalent(planMgr, query);

    setSearchResults(results);
    setSearching(false);
  };

  const handleDrain = async () => {
    await drainTalentPool(planMgr);
  };

  const mapping = doc.GetFieldMap(enumObjectTypes.holder);

  const myRole = planMgr.GetPlanRole();
  const canEdit = planMgr.CanUpdatePlan();
  const isOwner = myRole === "owner";
  const allowDrainPool = talentPool.length > 0 && isOwner;

  const searchOptions = searchResults
    .filter(item => item[IDField])
    .map(result => {
      const label = FormatEmployeeDictLabel(result, mapping);
      const nameField = mapping["Name"];

      return {
        label,
        value: result[IDField],
        dataTestId: `talent-pool-result-${result[nameField]}`,
        clickHandler: () => {
          addResultToTalentPool(planMgr, result);
        }
      };
    });

  return (
    <DropArea className="oc-fill-height" onDragEnd={handleDragEnd}>
      <Flex height="100%" direction="column">
        <div className={cx("sidebar-header-buttons", "sidebar-content-item--divider")}>
          <Flex direction="column" gap="4px" width="100%">
            <SearchMenu
              searchSize="small"
              inputProps={{
                ["data-testid"]: "talent-pool-search",
                disabled: !canEdit
              }}
              placeholder={appstr("Find record to add to talent pool…")}
              searchCallback={handleInput}
              loading={isSearching}
              options={searchOptions}
            />
          </Flex>
        </div>
        <Flex
          className={cx("sidebar-content-container", "ocn-list-scrollbar")}
          direction="column"
          flex={"1"}
          overflow="auto"
        >
          {talentPool.length === 0 ? (
            <Text colorModifier="grey" size="small">
              {appstr("stageNew")}
            </Text>
          ) : null}
          {talentPool.map((talent, index) => (
            <TalentDetail
              key={talent[IDField]}
              includeDivider={index > 0}
              planMgr={planMgr}
              talent={talent}
            />
          ))}
        </Flex>
        {allowDrainPool ? (
          <div className={cx("sidebar-content-container", "sidebar-content-item--divider")}>
            <Flex justifyContent="center" alignItems="end" gap="8px" padding="8px 0 0">
              <Text size="small" colorModifier="lightgrey">
                {talentPool.length} {talentPool.length === 1 ? appstr("Record") : appstr("Records")}
              </Text>
              <Button
                disabled={!canEdit}
                onClick={handleDrain}
                modifier="secondary"
                size={"xsmall"}
                tooltipProps={getHoverTipContent("Remove all records from the Talent Pool")}
              >
                {appstr("Clear all")}
              </Button>
            </Flex>
          </div>
        ) : null}
      </Flex>
    </DropArea>
  );
};
